// FieldNotes — slides in from the right at 25/50/75% progress with a
// reflection prompt tailored to the user's spot in the edition. Generic
// enough to work for every one of the 45 editions without per-edition
// authoring. Saves the user's notes to library_progress.field_notes.
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Sparkles, X, Save, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Universal prompts cycled through quartiles. Designed to feel like the
// editor leaning over and asking the right question.
const PROMPTS = [
  { at: 0.25, kicker: "Quarter in", prompt: "What's the first specific idea here that you'd want to try in the next 7 days?" },
  { at: 0.50, kicker: "Halfway", prompt: "Which sentence so far would you screenshot and send to a friend? Capture it." },
  { at: 0.75, kicker: "Three-quarters", prompt: "If you had to teach one of these moves on a podcast, which one would it be — and why?" },
];

export default function FieldNotes({ slug, currentPage, pageCount, isLightMode }) {
  const [activePrompt, setActivePrompt] = useState(null);
  const [shownIds, setShownIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  // Trigger prompts when progress crosses a quartile
  useEffect(() => {
    if (!pageCount || pageCount < 4) return;
    const pct = currentPage / pageCount;
    for (const p of PROMPTS) {
      const id = `${slug}_${p.at}`;
      if (pct >= p.at && !shownIds.has(id) && !readSeen(id)) {
        setActivePrompt(p);
        setOpen(true);
        setShownIds((s) => new Set([...s, id]));
        markSeen(id);
        break;
      }
    }
  }, [currentPage, pageCount, slug, shownIds]);

  // Autosave note 800ms after typing stops
  useEffect(() => {
    if (!activePrompt || !note) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.post(
          `${API}/api/library/field-notes/${slug}`,
          { quartile: activePrompt.at, prompt: activePrompt.prompt, note },
          { withCredentials: true }
        );
        setSavedAt(new Date());
      } catch {
        // silent — notes also live in localStorage
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [note, activePrompt, slug]);

  // Persist to localStorage as a safety net
  useEffect(() => {
    if (!activePrompt) return;
    try {
      localStorage.setItem(`mm_note_${slug}_${activePrompt.at}`, note);
    } catch { /* noop */ }
  }, [note, activePrompt, slug]);

  // Restore prior note when prompt opens
  useEffect(() => {
    if (!activePrompt) return;
    try {
      const prev = localStorage.getItem(`mm_note_${slug}_${activePrompt.at}`) || "";
      setNote(prev);
    } catch { /* noop */ }
  }, [activePrompt, slug]);

  if (!activePrompt || !open) return null;

  const panelBg = isLightMode ? "bg-white text-ink" : "bg-[#1c1c1e] text-cream";
  const ring = isLightMode ? "border-ink/15" : "border-cream/15";
  const muted = isLightMode ? "text-ink/55" : "text-cream/55";

  return (
    <div
      className="fixed inset-x-0 bottom-0 sm:right-4 sm:left-auto sm:bottom-4 sm:max-w-sm z-40"
      data-testid="field-notes"
    >
      <div className={`${panelBg} border ${ring} shadow-2xl p-5 sm:p-6`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 opacity-70" />
            <p className={`text-[10px] tracking-[0.3em] uppercase ${muted}`}>
              The editor · {activePrompt.kicker}
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className={`${muted} hover:opacity-100 opacity-70 p-1`}
            aria-label="Close prompt"
            data-testid="field-notes-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="font-serif italic text-lg leading-snug mb-3">
          {activePrompt.prompt}
        </p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Type a line or two — it's saved to your shelf."
          rows={3}
          data-testid="field-notes-input"
          className={`w-full bg-transparent border ${ring} focus:outline-none focus:border-current/60 p-3 text-sm leading-relaxed font-serif`}
        />
        <div className="mt-2 flex items-center justify-between text-[10px]">
          <span className={muted}>
            {savedAt ? (
              <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>
            ) : (
              <span className="inline-flex items-center gap-1"><Save className="h-3 w-3" /> Autosaves as you type</span>
            )}
          </span>
          <button
            onClick={() => setOpen(false)}
            className="overline tracking-[0.25em] opacity-70 hover:opacity-100"
            data-testid="field-notes-done"
          >
            Got it →
          </button>
        </div>
      </div>
    </div>
  );
}

// Tiny per-session seen-tracker (so prompts don't repeat in same session)
function readSeen(id) {
  try { return !!sessionStorage.getItem(`mm_seen_${id}`); } catch { return false; }
}
function markSeen(id) {
  try { sessionStorage.setItem(`mm_seen_${id}`, "1"); } catch { /* noop */ }
}
